import {
  Accordion,
  AccordionItem,
  Button,
  FilterableMultiSelect,
  Layer,
} from '@carbon/react';
import { FilterReset } from '@carbon/react/icons';
import { format0 } from 'utils/number';
import type { CatalogFacetBucket } from './hooks';
import styles from './add-wells-tearsheet.module.scss';

export interface AddWellsFiltersProps {
  statusBuckets: CatalogFacetBucket[];
  operatorBuckets: CatalogFacetBucket[];
  statusIds: string[];
  operatorIds: string[];
  onChangeStatus: (ids: string[]) => void;
  onChangeOperator: (ids: string[]) => void;
  onReset: () => void;
}

interface FacetSelectProps {
  id: string;
  titleText: string;
  placeholder: string;
  buckets: CatalogFacetBucket[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}

/**
 * One facet as a searchable multi-select of string ids, each labelled with its name and count.
 * String items (not objects) so Carbon's selection + filtering behave, mirroring the mock.
 * @param {FacetSelectProps} props The facet options and the current selection.
 * @return {JSX.Element} The facet dropdown.
 */
function FacetSelect({
  id,
  titleText,
  placeholder,
  buckets,
  selectedIds,
  onChange,
}: FacetSelectProps) {
  const labels = new Map(buckets.map((bucket) => [bucket.id, bucket.name]));
  const counts = new Map(buckets.map((bucket) => [bucket.id, bucket.count]));
  const items = buckets.map((bucket) => bucket.id);

  return (
    <FilterableMultiSelect
      id={id}
      titleText={titleText}
      placeholder={placeholder}
      items={items}
      itemToString={(value: string | null) =>
        value
          ? `${labels.get(value) ?? value} (${format0(counts.get(value) ?? 0)})`
          : ''
      }
      selectedItems={selectedIds}
      onChange={({ selectedItems }: { selectedItems: string[] }) =>
        onChange(selectedItems)
      }
      selectionFeedback="top-after-reopen"
      autoAlign
      size="md"
    />
  );
}

/**
 * The Add-wells Filters sidebar: a Reset Filters control, then a "Filters" accordion holding the
 * Well Status and Operator searchable multi-selects — matching the reconcile mock.
 * @param {AddWellsFiltersProps} props The facet buckets, selections, handlers and reset.
 * @return {JSX.Element} The filters sidebar.
 */
export function AddWellsFilters({
  statusBuckets,
  operatorBuckets,
  statusIds,
  operatorIds,
  onChangeStatus,
  onChangeOperator,
  onReset,
}: AddWellsFiltersProps) {
  return (
    <aside className={styles.filters} aria-label="Filter catalog wells">
      <div className={styles.filtersHeader}>
        <Button
          kind="ghost"
          renderIcon={FilterReset}
          onClick={onReset}
          className={styles.resetFilters}
        >
          Reset Filters
        </Button>
      </div>
      <Accordion className={styles.filterSections}>
        <AccordionItem title="Filters" open>
          <Layer className={styles.filterFields}>
            <FacetSelect
              id="add-wells-status-filter"
              titleText="Well Status"
              placeholder="Search well status"
              buckets={statusBuckets}
              selectedIds={statusIds}
              onChange={onChangeStatus}
            />
            <FacetSelect
              id="add-wells-operator-filter"
              titleText="Operator"
              placeholder="Search operators"
              buckets={operatorBuckets}
              selectedIds={operatorIds}
              onChange={onChangeOperator}
            />
          </Layer>
        </AccordionItem>
      </Accordion>
    </aside>
  );
}
